import type { Component } from '../router';
import { getSurface, onSurfaceChange, type Surface } from '../utils/surface';

// SurfaceGate is the variant mechanism: a component declares a
// capture-surface build and a desk-surface build, and the gate mounts whichever
// the current surface calls for, swapping live if the viewport crosses the
// phone/tablet breakpoint.
//
// It is the two-variant cousin of ViewportGate. Most components have exactly two
// real designs (field vs office), not three — SurfaceGate lets them say so
// without the `tablet: X, desktop: X` duplication a ternary gate forces, since
// the surface model already routes tablet → desk.

export type SurfaceFactories = {
  capture: () => Component;
  desk: () => Component;
};

export class SurfaceGate implements Component {
  private host: HTMLElement | null = null;
  private active: Component | null = null;
  private current: Surface | null = null;
  private dispose: (() => void) | null = null;

  constructor(private factories: SurfaceFactories) {}

  mount(host: HTMLElement): void {
    this.host = host;
    this.render(getSurface());
    this.dispose = onSurfaceChange((s) => this.render(s));
  }

  unmount(): void {
    if (this.active?.unmount) this.active.unmount();
    this.active = null;
    if (this.dispose) this.dispose();
    this.dispose = null;
    this.host = null;
    this.current = null;
  }

  private render(surface: Surface): void {
    if (!this.host) return;
    if (surface === this.current) return;
    if (this.active?.unmount) this.active.unmount();
    this.host.innerHTML = '';
    this.active = this.factories[surface]();
    this.active.mount(this.host);
    this.current = surface;
  }
}
